import { id } from "./util";

type Context = {
  allowed: boolean;
  visitorId: string;
  sessionId: string;
  marketing: boolean;
  receipt?: { id: string; proof: string };
};
type ElementSummary = { id: string; exposed: boolean; clicks: number };
type Summary = {
  pageId: string;
  pageViewId: string;
  visitorId: string;
  sessionId: string;
  revision: number;
  startedAt: string;
  path: string;
  version: string;
  viewportWidth: number;
  viewportHeight: number;
  documentHeight: number;
  activeMs: number;
  maxScroll: number;
  clickCount: number;
  clicks: Array<{ x: number; y: number; height: number }>;
  clicksTruncated: boolean;
  elements: ElementSummary[];
  source: string;
};
const LABEL = /^[a-z][a-z0-9_-]{0,63}$/;
const safe = (fn: () => void) => () => {
  try {
    fn();
  } catch {
    /* Host page must keep working. */
  }
};

/** Optional, memory-only recorder. Every callback rechecks consent and the route. */
export class BehaviorRecorder {
  private current: Summary | null = null;
  private epoch = 0;
  private observer: IntersectionObserver | null = null;
  private observed = new Set<Element>();
  private lastTick = Date.now();
  private lastActivity = Date.now();
  private lastSent = "";
  private lastConfig = 0;
  private blocked = false;
  private busy = false;
  private failures = 0;
  private retryAt = 0;
  private route = "";
  private visible = document.visibilityState === "visible";

  constructor(
    private url: string,
    private key: string,
    private context: () => Context,
  ) {
    const activity = safe(() => {
      this.tick();
      this.lastActivity = Date.now();
    });
    document.addEventListener(
      "click",
      (event) => {
        try {
          this.click(event);
        } catch {
          /* noop */
        }
      },
      true,
    );
    addEventListener("scroll", activity, { passive: true });
    document.addEventListener("keydown", activity, { passive: true });
    document.addEventListener("pointerdown", activity, { passive: true });
    document.addEventListener(
      "visibilitychange",
      safe(() => {
        this.tick();
        this.visible = document.visibilityState === "visible";
        if (document.visibilityState === "hidden") this.checkpoint(true);
        else {
          this.lastTick = this.lastActivity = Date.now();
          this.observe();
        }
      }),
    );
    addEventListener(
      "pagehide",
      safe(() => this.checkpoint(true)),
    );
    addEventListener(
      "pageshow",
      safe(() => this.navigate()),
    );
    addEventListener(
      "popstate",
      safe(() => this.navigate()),
    );
    addEventListener(
      "tmg:locationchange",
      safe(() => this.navigate()),
    );
    document.addEventListener(
      "DOMContentLoaded",
      safe(() => this.navigate()),
    );
    // Only watch the version attribute, never serialize the DOM or its mutations.
    if (typeof MutationObserver !== "undefined")
      new MutationObserver(safe(() => this.navigate())).observe(
        document.documentElement,
        { attributes: true, attributeFilter: ["data-tmg-page-version"] },
      );
    setInterval(
      safe(() => {
        if (!this.context().allowed) {
          this.clear();
          return;
        }
        this.tick();
      }),
      5000,
    );
    setInterval(
      safe(() => {
        if (!this.context().allowed) {
          this.clear();
          return;
        }
        this.observe();
        this.checkpoint(false);
        if (
          !this.current &&
          !this.blocked &&
          Date.now() - this.lastConfig > 60_000
        )
          this.navigate();
      }),
      30_000,
    );
    this.navigate();
  }

  private clear() {
    this.current = null;
    this.observer?.disconnect();
    this.observer = null;
    this.observed.clear();
    this.lastSent = "";
    this.busy = false;
    this.epoch++;
  }
  refreshConsent() {
    const c = this.context();
    if (
      this.current &&
      this.allowed() &&
      c.visitorId === this.current.visitorId &&
      c.sessionId === this.current.sessionId &&
      this.source(c.marketing) === this.current.source
    )
      return;
    this.restart();
  }
  restart() {
    this.checkpoint(true, true);
    this.clear();
    this.route = "";
    this.blocked = false;
    this.navigate();
  }
  navigate() {
    const path = location.pathname;
    const version =
      document.documentElement.getAttribute("data-tmg-page-version") || "";
    const route = `${path}|${version}`;
    if (this.current && this.route === route) return;
    this.checkpoint(true, true);
    // A route transition discards old visual state; it must never observe the new DOM as the old page.
    this.clear();
    this.route = route;
    if (!this.context().allowed || this.blocked || !LABEL.test(version)) return;
    const epoch = this.epoch;
    this.lastConfig = Date.now();
    void fetch(this.url, {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({
        op: "config",
        key: this.key,
        analytics: true,
        receipt: this.context().receipt,
        path,
        version,
      }),
    })
      .then(async (r) => {
        if (!r.ok) return null;
        return r.json() as Promise<{
          config: { pageId: string; elements: string[] } | null;
        }>;
      })
      .then((response) => {
        if (
          epoch !== this.epoch ||
          !this.context().allowed ||
          !response?.config
        )
          return;
        const c = this.context();
        const now = Date.now();
        this.current = {
          pageId: response.config.pageId,
          pageViewId: id("bpv"),
          visitorId: c.visitorId,
          sessionId: c.sessionId,
          revision: 0,
          startedAt: new Date(now).toISOString(),
          path,
          version,
          viewportWidth: Math.round(innerWidth),
          viewportHeight: Math.round(innerHeight),
          documentHeight: this.height(),
          activeMs: 0,
          maxScroll: 0,
          clickCount: 0,
          clicks: [],
          clicksTruncated: false,
          elements: response.config.elements
            .filter((e) => LABEL.test(e))
            .slice(0, 30)
            .map((e) => ({ id: e, exposed: false, clicks: 0 })),
          source: this.source(c.marketing),
        };
        this.lastTick = this.lastActivity = now;
        this.visible = document.visibilityState === "visible";
        this.failures = 0;
        this.retryAt = 0;
        this.observe();
        this.checkpoint(false);
      })
      .catch(() => {
        /* Retry config on the next bounded interval. */
      });
  }
  private source(marketing: boolean) {
    if (!marketing) return "unknown";
    try {
      const s = new URL(location.href).searchParams
        .get("utm_source")
        ?.toLowerCase();
      return s && /^[a-z0-9_.-]{1,64}$/.test(s) ? s : "unknown";
    } catch {
      return "unknown";
    }
  }
  private height() {
    return Math.min(
      100000,
      Math.max(
        innerHeight,
        document.documentElement.scrollHeight,
        document.body?.scrollHeight || 0,
      ),
    );
  }
  private allowed() {
    return (
      !!this.current &&
      this.context().allowed &&
      location.pathname === this.current.path &&
      document.documentElement.getAttribute("data-tmg-page-version") ===
        this.current.version
    );
  }
  private tick() {
    const now = Date.now();
    if (this.allowed() && this.current) {
      if (this.visible) {
        this.current.activeMs = Math.min(
          3_600_000,
          this.current.activeMs +
            Math.max(
              0,
              Math.min(now, this.lastActivity + 30_000) - this.lastTick,
            ),
        );
        const height = this.height();
        this.current.documentHeight = height;
        const depth = Math.min(
          100,
          Math.floor(((scrollY + innerHeight) / height) * 20) * 5,
        );
        this.current.maxScroll = Math.max(this.current.maxScroll, depth);
      }
    }
    this.lastTick = now;
  }
  private observe() {
    if (
      !this.allowed() ||
      !this.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    if (!this.observer)
      this.observer = new IntersectionObserver((entries) => {
        try {
          if (
            !this.allowed() ||
            !this.current ||
            document.visibilityState !== "visible"
          )
            return;
          for (const entry of entries)
            if (
              entry.isIntersecting &&
              entry.intersectionRect.height > 0 &&
              entry.intersectionRect.width > 0
            ) {
              const el = this.current.elements.find(
                (e) => e.id === entry.target.getAttribute("data-tmg-id"),
              );
              if (el) el.exposed = true;
            }
        } catch {
          /* noop */
        }
      });
    for (const node of this.observed) {
      if (node.isConnected === false) {
        this.observer.unobserve(node);
        this.observed.delete(node);
      }
    }
    for (const e of this.current.elements)
      for (const node of Array.from(
        document.querySelectorAll(`[data-tmg-id="${e.id}"]`),
      ).slice(0, 10)) {
        if (!this.observed.has(node)) {
          this.observed.add(node);
          this.observer.observe(node);
        }
        // Re-evaluate on visibility resume, when an existing observer may not fire.
        if (document.visibilityState === "visible") {
          const r = node.getBoundingClientRect();
          if (
            r.width > 0 &&
            r.height > 0 &&
            r.bottom > 0 &&
            r.top < innerHeight &&
            r.right > 0 &&
            r.left < innerWidth
          )
            e.exposed = true;
        }
      }
  }
  private click(event: MouseEvent) {
    if (!this.allowed() || !this.current || !event.isTrusted) return;
    this.tick();
    this.lastActivity = Date.now();
    this.current.clickCount = Math.min(10000, this.current.clickCount + 1);
    const target = event.target instanceof Element ? event.target : null;
    // Count each registered ancestor, allowing a CTA to belong to a measured section.
    let fixed = false;
    for (
      let node = target, n = 0;
      node && n < 20;
      node = node.parentElement, n++
    ) {
      const e = this.current.elements.find(
        (el) => el.id === node!.getAttribute("data-tmg-id"),
      );
      if (e) e.clicks = Math.min(10000, e.clicks + 1);
      const position = getComputedStyle(node).position;
      if (position === "fixed" || position === "sticky") fixed = true;
    }
    // Keyboard activations have no useful pointer coordinate.
    if (
      fixed ||
      event.detail === 0 ||
      Math.abs(innerWidth - this.current.viewportWidth) > 20
    )
      return;
    if (this.current.clicks.length >= 100) {
      this.current.clicksTruncated = true;
      return;
    }
    const height = this.height();
    this.current.clicks.push({
      x: Math.min(
        100,
        Math.max(0, Math.round((event.clientX / innerWidth) * 100)),
      ),
      y: Math.min(
        200,
        Math.max(0, Math.round(((event.clientY + scrollY) / height) * 200)),
      ),
      height,
    });
  }
  private checkpoint(final: boolean, snapshotOnly = false) {
    // On navigation, send only previously measured state; never inspect the new DOM.
    if (
      !(snapshotOnly ? this.context().allowed : this.allowed()) ||
      !this.current ||
      this.current.revision >= 1000 ||
      (!final && (this.busy || Date.now() < this.retryAt))
    )
      return;
    if (!snapshotOnly) this.tick();
    const state = this.current;
    const fingerprint = JSON.stringify({ ...state, revision: 0 });
    if (fingerprint === this.lastSent) return;
    state.revision++;
    const body = JSON.stringify({
      op: "checkpoint",
      key: this.key,
      analytics: true,
      receipt: this.context().receipt,
      summary: state,
    });
    if (body.length > 15000) return;
    const epoch = this.epoch;
    this.busy = true;
    void fetch(this.url, {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: final,
    })
      .then((r) => {
        if (epoch !== this.epoch) return;
        if (r.ok) {
          this.lastSent = fingerprint;
          this.failures = 0;
        } else if (r.status === 403 || r.status === 404) {
          this.blocked = true;
          this.clear();
        } else throw new Error("retry");
      })
      .catch(() => {
        if (epoch !== this.epoch) return;
        this.failures++;
        this.retryAt =
          Date.now() + Math.min(300000, 5000 * 2 ** Math.min(this.failures, 6));
      })
      .finally(() => {
        if (epoch === this.epoch) this.busy = false;
      });
  }
}
